import PropTypes from 'prop-types';
import { memo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// @mui
import { Stack, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
//
import { NavListRoot } from './NavList';
import { getFlashMessages } from '../../../redux/slices/operator';
import { useNavbarMargin } from '../../../contexts/NavbarMarginContext';

// ----------------------------------------------------------------------

const hideScrollbar = {
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

NavSectionHorizontal.propTypes = {
  navConfig: PropTypes.array,
};

function NavSectionHorizontal({ navConfig }) {
  const dispatch = useDispatch();
  const { navbarMargin = 0.75, navbarPreset = 1 } = useNavbarMargin() || {}; // Get both margin and preset level

  // Helper functions to avoid nested ternary expressions
  const getMarginBottomValue = (preset) => {
    if (preset >= 3) {
      return `${preset * 4}px`;
    }
    if (preset === 2) {
      return '10px';
    }
    return 0;
  };

  const getBottomPositionValue = (preset) => {
    if (preset >= 3) {
      return `${preset * 3}px`;
    }
    if (preset === 2) {
      return '8px';
    }
    return 0;
  };

  // Helper function to calculate bottom padding based on preset level
  const getBottomPadding = (preset, margin) => {
    if (preset >= 4) {
      return preset * 1.5; // Significant padding for highest settings (4-5)
    }
    if (preset === 3) {
      return margin * 1.3; // Moderate padding for spacious setting (3)
    }
    if (preset === 2) {
      return margin; // Use the actual margin value for Comfortable (2)
    }
    return 0.75; // Default padding for Compact (1)
  };

  // Helper to calculate padding bottom to replace nested ternary
  const getPaddingBottom = (preset, margin) => {
    if (preset === 2) {
      return 1.5; // Comfortable - specific value for level 2
    }
    return getBottomPadding(preset, margin);
  };

  // Fetch messages from Redux state
  const messages = useSelector((state) => state?.operator?.flashMessages?.data || []);

  useEffect(() => {
    dispatch(getFlashMessages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Track loading state for the messages
  const scrollerRef = useRef(null); // Ref to access the scroll box

  // Combine messages into a single scrolling string
  const scrollerContent = messages.map((msg) => msg?.message).join(' | ');

  // Calculate animation duration based on the length of the messages
  const scrollSpeed = scrollerContent.length > 0 ? Math.max(10, scrollerContent.length * 0.1) : 50; // Fast scrolling

  const toggleAnimation = () => {
    setIsPaused((prev) => !prev);
  };

  // Reset the scroll position when the animation ends
  const handleAnimationEnd = () => {
    if (scrollerRef.current) {
      scrollerRef.current.style.animation = 'none'; // Remove animation
      // Force reflow to reset animation
      // eslint-disable-next-line no-void
      void scrollerRef.current.offsetWidth;
      scrollerRef.current.style.animation = `scroll ${scrollSpeed}s linear infinite`; // Reapply animation
    }
  };

  useEffect(() => {
    // If messages have been loaded, stop the loading indicator
    if (messages.length > 0) {
      setIsLoading(false);
    }
  }, [messages]); return (
    <Stack direction="column"
      sx={{
        bgcolor: 'background.neutral',
        borderRadius: 2,
        boxShadow: (theme) => `0 2px 8px -2px ${alpha(theme.palette.grey[500], 0.16)}`, overflow: 'hidden',
        transition: 'all 0.3s ease',
        // Use getMarginBottom helper instead of nested ternary
        marginBottom: {
          xs: getMarginBottomValue(navbarPreset),
          sm: 0  // No extra margin on larger screens
        },
        position: { xs: 'relative', sm: 'static' }, // Ensure positioning context
        // Use getBottomPosition helper instead of nested ternary
        bottom: {
          xs: getBottomPositionValue(navbarPreset),
          sm: 0  // No positioning on larger screens
        },
        '&:hover': {
          boxShadow: (theme) => `0 4px 12px -4px ${alpha(theme.palette.grey[500], 0.2)}`,
        },
      }}
    >
      {/* Infinite Scroller */}
      {messages.length > 0 &&
        !isLoading && (
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 1.5,
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.08)}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: (theme) => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0)}, ${alpha(theme.palette.primary.lighter, 0.12)})`,
                opacity: 0,
                transition: 'opacity 0.3s ease',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: (theme) => `
                  inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)},
                  0 4px 8px -4px ${alpha(theme.palette.primary.main, 0.12)}
                `,
                '&::before': {
                  opacity: 1,
                },
              },
            }}
          >
            <Box
              ref={scrollerRef}
              onClick={toggleAnimation}
              onAnimationEnd={handleAnimationEnd}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '100%',
                animation: `scroll ${scrollSpeed}s linear infinite`,
                animationPlayState: isPaused ? 'paused' : 'running',
                cursor: 'pointer',
                px: 2,
                '@keyframes scroll': {
                  from: { transform: 'translateX(100%)' },
                  to: { transform: 'translateX(-100%)' },
                }, '& span': {
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: (theme) => theme.palette.primary.main,
                  typography: 'subtitle2',
                  fontSize: { xs: '13px', sm: '14px' },
                  fontWeight: 'medium',
                  letterSpacing: '0.01em',
                  textShadow: '0 0 1px rgba(0,0,0,0.1)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: (theme) => theme.palette.primary.main,
                    display: 'inline-block',
                    margin: '0 8px',
                    boxShadow: (theme) => `0 0 4px ${theme.palette.primary.main}`,
                    animation: 'pulse 2s infinite',
                  },
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      opacity: 0.7,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                      opacity: 1,
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 0.7,
                    },
                  }
                }
              }}
            >
              <span>{scrollerContent}</span>
            </Box>
          </Box>
        )}

      {/* Navigation Items */}
      <Stack
        direction="row"
        justifyContent="center"
        sx={{
          py: {
            xs: 0.75, // Keep top padding consistent regardless of settings
            sm: navbarMargin
          },
          pt: {
            xs: 0.75, // Always use the default top padding
            sm: navbarMargin
          }, pb: {
            xs: getPaddingBottom(navbarPreset, navbarMargin), // Use helper instead of ternary
            sm: navbarMargin
          },
          px: 1,
          ...hideScrollbar,
          '& .MuiButtonBase-root': {
            minWidth: { xs: '64px', sm: '72px' },
            px: { xs: 0.5, sm: 1 },
            py: 0.5, // Keep consistent button padding regardless of settings
            borderRadius: 1.5,
            typography: 'caption',
            fontSize: '12px',
            fontWeight: 'medium',
            color: 'text.secondary',
            transition: (theme) => theme.transitions.create(['all'], {
              duration: '0.3s',
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            }),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: '2px',
              backgroundColor: 'primary.main',
              transition: 'width 0.3s ease',
            },
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.lighter,
              color: 'primary.main',
              transform: 'translateY(-2px)',
              '& svg': {
                color: 'primary.main',
                transform: 'scale(1.2) translateY(-2px)',
              },
              '&::before': {
                width: '80%',
              },
            },
            '&.active': {
              color: 'primary.main',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              fontWeight: 'bold',
              '& svg': {
                color: 'primary.main',
                transform: 'scale(1.1)',
              },
              '&::before': {
                width: '80%',
              },
            },
            '& svg': {
              transition: (theme) => theme.transitions.create(['all'], {
                duration: '0.3s',
                easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              }),
              mb: 0.5,
              color: 'inherit', // Ensure icons use the inherited color instead of blue
            },
          },
        }}
      >
        {navConfig.map((group, i) => (
          <Stack
            key={group.subheader || i}
            direction="row"
            flexShrink={0}
            flexGrow={1}
            spacing={0.5}
            justifyContent="center"
          >
            {group.items.map((list) => (
              <NavListRoot
                key={list.title + list.path}
                list={list}
                sx={{
                  flexGrow: 1,
                  maxWidth: { xs: '72px', sm: '80px' }
                }}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

export default memo(NavSectionHorizontal);
